import type { CreditActionDisclosure } from '@vibecontrol/shared';

// Owner decision 2026-07-17 (issue #981): Blueprint customers run on sponsored
// credits, so no credit-spend copy is shown anywhere in this app for now. The
// disclosure payload still flows (parsing/state stay wired) so flipping this
// back on is a one-line change when credits become customer-visible.
export const SHOW_CUSTOMER_CREDIT_DISCLOSURE = false;

const DISCLOSURE_KEYS = new Set([
  'version',
  'aiExecutionRunId',
  'audience',
  'status',
  'capability',
  'billingSurface',
  'estimatedCredits',
  'costCapCredits',
  'costCapSource',
  'actualCredits',
  'periodSpent',
  'monthlyCeiling',
]);

export function parseCustomerCreditDisclosure(value: unknown): CreditActionDisclosure | null {
  if (!isRecord(value) || Object.keys(value).some((key) => !DISCLOSURE_KEYS.has(key))) {
    return null;
  }
  if (
    value.version !== 1
    || value.audience !== 'customer'
    || value.billingSurface !== 'vibecontrol_credits'
    || !isNonEmptyString(value.aiExecutionRunId)
    || !isNonEmptyString(value.capability)
    || !isNonEmptyString(value.costCapSource)
    || !isNonNegativeNumber(value.estimatedCredits)
    || !isNonNegativeNumber(value.costCapCredits)
    || !isNullableNonNegativeNumber(value.periodSpent)
    || !isNullableNonNegativeNumber(value.monthlyCeiling)
  ) {
    return null;
  }
  if (value.status === 'estimated') {
    if (value.actualCredits !== null) return null;
  } else if (value.status === 'completed' || value.status === 'failed') {
    if (!isNonNegativeNumber(value.actualCredits)) return null;
  } else {
    return null;
  }
  return value as unknown as CreditActionDisclosure;
}

export function creditDisclosureCopy(
  disclosure: CreditActionDisclosure,
): { title: string; detail: string } {
  if (disclosure.status === 'estimated') {
    return {
      title: `Estimated ${formatCredits(disclosure.estimatedCredits)} credits`,
      // #2555: the per-call cost cap is an internal guard, never customer copy.
      detail: 'Before this hosted action',
    };
  }

  const meter = disclosure.periodSpent == null
    ? 'Monthly usage unavailable'
    : disclosure.monthlyCeiling == null
      ? `${formatCredits(disclosure.periodSpent)} credits used this month`
      : `${formatCredits(disclosure.periodSpent)} of ${formatCredits(disclosure.monthlyCeiling)} credits used this month`;

  if (disclosure.status === 'failed' && disclosure.actualCredits === 0) {
    return { title: 'No credits used', detail: meter };
  }
  if (disclosure.status === 'failed') {
    return {
      title: `Used ${formatCredits(disclosure.actualCredits ?? 0)} credits before this action stopped`,
      detail: meter,
    };
  }
  return {
    title: `Used ${formatCredits(disclosure.actualCredits ?? 0)} credits`,
    detail: meter,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isNullableNonNegativeNumber(value: unknown): value is number | null {
  return value === null || isNonNegativeNumber(value);
}

function formatCredits(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
}
